import { useCallback, useState } from 'react';
import fleetApi from '../../services/api.service';

/**
 * Hook custom pour optimiser les livraisons non affectées avec VROOM
 * 
 * Retourne:
 * - optimizeAllSteps: fonction pour lancer l'optimisation
 * - isOptimizing: état de chargement
 * - error: erreur éventuelle
 * - optimizationResult: résultats de l'optimisation (routes, summary, unassignedJobs)
 * - resetOptimization: fonction pour réinitialiser les résultats
 */
const useOptimizeUnassignedSteps = () => {
  const [isOptimizing, setIsOptimizing] = useState(false);
  const [error, setError] = useState(null);
  const [optimizationResult, setOptimizationResult] = useState(null);

  const optimizeAllSteps = useCallback(async ({ tourDate } = {}) => {
    setIsOptimizing(true);
    setError(null);
    setOptimizationResult(null);

    try {
      const payload = {};
      if (tourDate) {
        payload.tour_date = tourDate;
      }

      const response = await fleetApi.post('/tours-planning/optimize-all', payload);

      if (response.status === 200 && response.data) {
        // Extraire les données de l'optimisation
        const optimizationData = response.data?.data ?? response.data;
        
        // Structure des données retournées:
        // {
        //   routes: [...],         // Détails de chaque tournée
        //   summary: {...},        // Résumé global (distance, durée, etc.)
        //   unassignedJobs: [...]  // Commandes non affectées
        // }
        
        setOptimizationResult(optimizationData);
        return {
          success: true,
          data: optimizationData,
          message: response.data?.message || 'Optimisation réussie'
        };
      }

      throw new Error('Réponse invalide du serveur');
    } catch (err) {
      const message = err?.response?.data?.message || err.message || "Impossible d'optimiser les livraisons";
      setError(message);
      throw new Error(message);
    } finally {
      setIsOptimizing(false);
    }
  }, []);

  const resetOptimization = useCallback(() => {
    setOptimizationResult(null);
    setError(null);
  }, []);

  return {
    optimizeAllSteps,
    isOptimizing,
    error,
    optimizationResult,
    resetOptimization,
  };
};

export default useOptimizeUnassignedSteps;

